import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { fetchDeployments, fetchSnowDepthData } from '../../services/api';
import 'chart.js/auto';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Chart } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { SnowData, DeploymentDate, ChartData } from '../../types/types';
import { annotations, legendData } from '../../utils/chart';
import { findDeploymentByDate } from '../../utils/deployment';

interface SnowDepthChartProps {
  selectedDate: string | null;
  setSelectedDeployment: (deploymentName: string) => void;
  setSelectedDate: (date: string) => void;
  isDark: boolean;
}

Chart.register(annotationPlugin);

const adjustMarchData = (data: SnowData[]): SnowData[] => {
  return data.map((entry) => {
    if (entry.date === '2025-03-08') {
      entry.snow_depth_mm = 1082.7;
    } else if (entry.date === '2025-03-09') {
      entry.snow_depth_mm = 1071.4;
    } else if (entry.date === '2024-11-28') {
      entry.snow_depth_mm = 220.0;
    }
    return entry;
  });
};

const fillMissingDates = (
  data: SnowData[],
  startDateStr: string,
  endDateStr: string
): SnowData[] => {
  const snowMap = new Map(data.map((d) => [d.date, d.snow_depth_mm]));
  const result: SnowData[] = [];
  let current = new Date(startDateStr);
  const end = new Date(endDateStr);
  while (current <= end) {
    const isoDate = current.toISOString().slice(0, 10);
    result.push({
      date: isoDate,
      snow_depth_mm: snowMap.get(isoDate) ?? 0,
    });
    current.setDate(current.getDate() + 1);
  }
  return result;
};

const SnowDepthChart: React.FC<SnowDepthChartProps> = ({
  selectedDate,
  setSelectedDeployment,
  setSelectedDate,
  isDark,
}) => {
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [snowData, setSnowData] = useState<SnowData[]>([]);
  const [deploymentDates, setDeploymentDates] = useState<DeploymentDate[]>([]);

  useEffect(() => {
    fetchSnowDepthData()
      .then((data: SnowData[]) => {
        const filledData = fillMissingDates(data, '2024-10-01', '2025-11-18');
        const adjustedData = adjustMarchData(filledData);
        setSnowData(adjustedData);
      })
      .catch((err) => console.error('Error loading snow data:', err));

    fetchDeployments()
      .then((data: DeploymentDate[]) => {
        setDeploymentDates(data);
      })
      .catch((err) => console.error('Error loading deployment data:', err));
  }, []);

  useEffect(() => {
    if (snowData.length === 0 || deploymentDates.length === 0) return;

    const dates = snowData.map((entry) => entry.date);
    const depthsInCm = snowData.map((entry) => entry.snow_depth_mm / 10);

    const pointColors = dates.map((date) => {
      const isSelected = date === selectedDate;
      const isDeploymentDate = deploymentDates.some((d) => d.date === date);
      return isSelected ? 'red' : isDeploymentDate ? 'blue' : 'gray';
    });

    const pointSizes = dates.map((date) => {
      const isSelected = date === selectedDate;
      const isDeploymentDate = deploymentDates.some((d) => d.date === date);
      return isSelected ? 8 : isDeploymentDate ? 6 : 0;
    });

    const borderColor = isDark ? 'white' : 'black';
    const backgroundColor = isDark
      ? 'rgba(255, 255, 255, 0.3)'
      : 'rgba(0, 0, 0, 0.3)';

    setChartData({
      labels: dates,
      datasets: [
        {
          label: '❄ Snow Depth (cm)',
          data: depthsInCm,
          borderColor: borderColor,
          backgroundColor: backgroundColor,
          tension: 0.3,
          fill: true,
          pointRadius: pointSizes,
          pointBackgroundColor: pointColors,
        },
      ],
    });
  }, [snowData, deploymentDates, selectedDate, isDark]);

  const handleClick = (event: any) => {
    const chart = event.chart;
    const elements = chart.getElementsAtEventForMode(
      event.native,
      'nearest',
      { intersect: true },
      false
    );
    if (elements.length > 0) {
      const elementIndex = elements[0].index;
      const clickedDate = chartData?.labels[elementIndex];
      const chosenDeployment = findDeploymentByDate(
        clickedDate || '',
        deploymentDates
      );
      if (chosenDeployment) {
        setSelectedDeployment(`Deployment_${chosenDeployment.number}`);
        setSelectedDate(chosenDeployment.date);
      }
    }
  };

  const filteredData = selectedDate
    ? snowData.filter((d) => d.date === selectedDate)
    : snowData;

  if (!chartData) return <p className={`text-center`}>Loading data...</p>;

  return (
    <div
      className={`dark:text-white ${isDark ? 'bg-neutral-900' : 'bg-white'}`}
    >
      <div
        className={`relative h-48 w-full ${isDark ? 'dark:bg-neutral-900' : 'bg-white'}`}
      >
        <Line
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            onClick: handleClick,
            scales: {
              x: {
                ticks: {
                  maxTicksLimit: 12,
                  color: isDark ? 'white' : 'black',
                },
                grid: {
                  color: isDark
                    ? 'rgba(255, 255, 255, 0.3)'
                    : 'rgba(0, 0, 0, 0.1)',
                },
                type: 'time',
                time: {
                  unit: 'month',
                },
              },
              y: {
                min: 0,
                ticks: {
                  callback: (value) => `${value} cm`,
                  color: isDark ? 'white' : 'black',
                },
                grid: {
                  color: isDark
                    ? 'rgba(255, 255, 255, 0.3)'
                    : 'rgba(0, 0, 0, 0.1)',
                },
              },
            },
            plugins: {
              legend: {
                display: true,
                onClick: () => {},
                labels: {
                  color: isDark ? 'white' : 'black',
                  generateLabels: function () {
                    return legendData.map((item) => ({
                      text: item.label,
                      fillStyle: item.backgroundColor,
                      fontColor: isDark ? 'white' : 'black',
                    }));
                  },
                },
              },
              annotation: {
                annotations,
              },
            },
          }}
        />
      </div>
      {selectedDate && filteredData.length > 0 && (
        <div
          className={`flex justify-center items-center p-4 mt-4 rounded-t-lg shadow-lg ${
            isDark
              ? 'dark:bg-neutral-900 dark:text-white'
              : 'bg-white text-black border-gray-300'
          }`}
        >
          <div className="text-center">
            <p className="font-semibold text-lg text-blue-500">
              Selected Deployment Date: {selectedDate}
            </p>
            <p className="text-md">
              ❄ Snow Depth: {filteredData[0].snow_depth_mm / 10} cm
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SnowDepthChart;
